import * as React from 'react';
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { supabase } from '../../../utils/supabase';
import Loader from '../../../components/Loader';
import * as Sentry from '@sentry/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { apiClient } from '../../../utils/openApiClient';
import { useOnboardingStore } from '../../../stores/useOnboardingStore';
import WelcomeLoading from '../../../components/onBoarding/WelcomeLoading';
import BaseInfo from '../../../components/onBoarding/BaseInfo';
import ChooseCategory from '../../../components/onBoarding/ChooseCategory';
import CustomizeCover from '../../../components/onBoarding/CustomizeCover';
import CustomizeGoals from '../../../components/onBoarding/CustomizeGoals';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Progress,
  Icon
} from '@virlewood/board-79';


export const Route = createFileRoute('/_on-boarding/on-boarding')({
  component: RouteComponent
});

const heroes = [
  {
    // Step 0 - Loading
    title: 'Benvenuto su Next is Free',
    subtitle: 'Prepareremo tutto in un attimo...'
  },
  {
    // Step 1 - Basic Info
    title: 'Creiamo il tuo profilo',
    subtitle: 'Questi dettagli aiuteranno i clienti a trovarti subito.'
  },
  {
    // Step 2 - Category
    title: 'Che tipo di attività gestisci?',
    subtitle: 'Seleziona la categoria che meglio descrive il tuo business'
  },
  {
    // Step 3 - Customize Cover
    title: 'Personalizza il tuo profilo',
    subtitle: "Scegli un colore e un'immagine che rappresentino il tuo brand"
  },
  {
    // Step 4 - Goals
    title: 'Imposta i traguardi',
    subtitle: 'Definisci i premi che i tuoi clienti potranno sbloccare'
  }
];

function RouteComponent() {

  const { user } = Route.useRouteContext();

  const { nextStep, previousStep, currentStep, data, resetOnboarding } = useOnboardingStore(state => state);

  const navigate = useNavigate();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  // Block navigation during onboarding and especially during submission
  useBlocker({
    shouldBlockFn: () => true,
    enableBeforeUnload: () => isSubmitting,
    disabled: currentStep === 0
  });

  // Preload category images
  useEffect(() => {
    const imagesToPreload = [
      '/illustrations/hamburger.webp',
      '/illustrations/parrucchieri.webp',
      '/illustrations/groceries.webp',
      '/illustrations/tools.webp',
      '/illustrations/shopping.webp',
      '/illustrations/planet.webp'
    ];

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Handle initial data loading
  useEffect(() => {
    // Set a small delay to show the welcome screen
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Handle final submission (when currentStep === 5 after Confirmation)
  useEffect(() => {
    if (currentStep === 5) {
      handleFinalSubmit();
    }
  }, [currentStep]);

  async function handleFinalSubmit() {
    setIsSubmitting(true);

    const storeData = useOnboardingStore.getState().data;

    // Upload cover image directly to Supabase Storage
    const coverDataUrl = storeData.cover_image || storeData.default_cover_image;
    if (coverDataUrl) {
      const res = await fetch(coverDataUrl);
      const blob = await res.blob();
      const { error: uploadError } = await supabase.storage
        .from('main')
        .upload(`${user.id}/logo.png`, blob, {
          contentType: blob.type || 'image/png',
          upsert: true
        });
      if (uploadError) {
        console.error('Cover image upload error:', uploadError);
      }
    }

    const body: any = {
      business_name: storeData.business_name,
      business_category: storeData.business_category,
      mainSalesPointAddress: storeData.mainSalesPointAddress,
      extraSalesPoints: storeData.extraSalesPoints || [],
      hasOneSalesPoint: storeData.hasOneSalesPoint ?? true,
      goals: storeData.goals || {},
      brand_color: storeData.brand_color || '#000000'
    };

    if (!storeData.hasOneSalesPoint) {
      body.mainSalesPointName = storeData.business_name;
    }

    const { data, error } = await apiClient.POST('/shop/simplified-creation', { body });

    if (error) {
      Sentry.captureException(error);
      toast.error('Si è verificato un errore. Riprova.');
      setIsSubmitting(false);
      return;
    }

    resetOnboarding();
    localStorage.removeItem('onboarding-storage');
    setSubmitMessage('Il tuo negozio è pronto!');
    setTimeout(() => {
      window.location.href = '/analytics?welcome_tour=true';
    }, 2000);
  }

  async function handleLogOut() {
    resetOnboarding();
    localStorage.removeItem('onboarding-storage');
    await supabase.auth.signOut();
    window.location.href = '/login';
  }

  // Render initial loading state
  if (isInitialLoading && currentStep === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Render submission loading state
  if (isSubmitting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        {submitMessage ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <Icon icon="hugeicons:checkmark-circle-02" className="text-primary mx-auto mb-4" style={{ fontSize: 64 }} />
            <h2 className="text-2xl font-semibold">{submitMessage}</h2>
          </motion.div>
        ) : (
          <>
            <Loader />
            <div className="text-center">
              <h2 className="text-2xl font-semibold">Stiamo creando il tuo negozio...</h2>
              <p className="text-muted-foreground mt-2">Non chiudere questa pagina</p>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col items-center px-4 justify-between relative">
      {/* Hero Section */}
      <div className={'w-fit flex items-center justify-between my-10 h-10 '}>
        {currentStep > 1 && currentStep <= 4 && (
          <Button onClick={previousStep} startIcon={'hugeicons:arrow-left-02'} iconOnly variant={'ghost'} />
        )}
        <Progress value={currentStep / 4 * 100} className={'bg-zinc-100 h-2 w-60'} />
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button iconOnly variant={'ghost'} startIcon={'hugeicons:more-horizontal'} disabled />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => { if (confirm('Sicuro di uscire?')) handleLogOut(); }} className={'text-destructive'}>
              <Icon className={'text-destructive'} icon={'hugeicons:logout-01'} /> Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className={'w-full'}>
        {currentStep > 0 && currentStep <= 3 && (
          <div className="text-center max-w-2xl mx-auto mb-10 text-balance">
            <motion.h1
              key={`title-${currentStep}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="font-semibold text-3xl md:text-4xl mb-3"
            >
              {heroes[currentStep].title}
            </motion.h1>
            <motion.p
              key={`subtitle-${currentStep}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-zinc-500"
            >
              {heroes[currentStep].subtitle}
            </motion.p>
          </div>
        )}
        {/* Step Components */}
        {currentStep === 0 && (
          <WelcomeLoading onComplete={() => nextStep()} />
        )}


        {currentStep === 1 && <BaseInfo />}

        {currentStep === 2 && <ChooseCategory />}

        {currentStep === 3 && <CustomizeCover />}

        {currentStep === 4 && <CustomizeGoals />}
      </div>
      <div className={'h-30'}></div>
      {import.meta.env.DEV && (
        <Button onClick={resetOnboarding} variant="outline" size="sm" className="fixed bottom-4 right-4">
          Clear All
        </Button>
      )}
    </div>
  )
    ;
}
